<template>
  <div>
    <h2>The War of Three Kingdoms in Ireland, 1641-48</h2>
    <h3>Background</h3>
    <h3>The Irish</h3>
    <p>
      Ireland has been home to native Irish, Scots and English since the 12th
      century. The native Irish, referred to as “mere Irish” by the English,
      lived for the most part under the rule of a local chief or noble. The
      succession for the noble class was not assured a smooth transition so
      there were numerous struggles and conflicts with associated shifting
      alliances and no central ruler. A relatively primitive form of agriculture
      and a semi-nomadic pastoral life following herds of cattle characterized
      the means of production in late medieval native Ireland with trade
      becoming more important in the early modern period. The native Irish
      retained their unique form of Catholicism that permitted divorce, trail
      marriage and married clergy after the Reformation and from the mid 16th
      century on looked to Spain and France for trade, clergy, and military
      assistance. By the middle of the 16th century, those local nobles who
      lived near the ever expanding English settled land, found themselves
      assigned English captains to serve as seneschals, or sheriffs, living in
      established strong houses on their property. These English captains were
      all to often more than willing to goad the Irish nobility into rash
      action, hoping it would result in more land lost to them as a penalty for
      rebellion and more gained by the English.
    </p>
    <h3>The Old English</h3>
    <p>
      The first Anglo-Norman settlements were established on the east coast in
      the 12th century and were militarily important to early England in order
      to control the sea which separated England and Ireland. As time went on,
      the English involved the Irish in their internal politics and continental
      adventures, occupying more and more land in an effort to civilize Ireland
      that did not include assimilation of the native Irish but rather insured
      their isolation. The English who settled in Ireland were seen as more
      Irish and less English by those who remained in England. The English
      Protestant Reformation and the consequent inclination of the English in
      Ireland to remain Catholics only increased this division. These, then,
      were known as the “Old English.” They represented a land owning
      aristocracy that, in the view of some authors, were becoming virtually
      indistinguishable from the native Irish by the 17th century. By the mid
      16th century their property rights were under threat. English recognized
      land titles could only be guaranteed by a system in which part of the land
      was surrendered to the English Crown. As the 17th century progressed,
      particularly after the Gunpowder Plot of 1607 and the subsequent decline
      of religious freedom, the Old English saw less representation in the Irish
      Privy Council and Parliament and a subsequent loss in influence over
      decisions being made about Ireland.
    </p>
    <h3>The Antrim Scots</h3>
    <p>
      Scottish mercenaries had served Irish nobles for several hundred years
      before they began to settle in Antrim in the 15th century. These were the
      “redshanks” of Clan Donald who were able to retain their lands in northern
      Antrim after the Lordship of the Western Isles was broken by Scotland in
      the 16th century. The Antrim Scots lived with and intermarried with the
      native Irish, retaining their Catholicism as well as their long standing
      blood feud with Clan Campbell who had engineered their loss of land and
      influence in Scotland. By the 17th century Randall MacDonnel, the Earl of
      Antrim, was accepting tenants on his land from Scotland, and, to a limited
      degree England as well.
      <br />
      <br />The importance of religion and its inherent political identity
      cannot be underestimated in 17th century society and in this regard
      Ireland was no different than the rest of Europe. The shared Catholicism
      of the native Irish, Old English and Antrim Scots created a strong basis
      for political and military cooperation.
    </p>
    <h3>The New English and New Scots</h3>
    <h4>The Munster Plantation</h4>
    <p>
      The Desmond rebellion, 1579-83, was seen by many English soldiers and
      administrators familiar with Ireland as a chance to acquire large tracts
      of land at the expense of the Irish rebels, even before the rebels were
      defeated. The Desmond rebellion was also the first time the Papacy and
      Spain took an active role in supporting the Irish and Old English, making
      this and all subsequent conflicts at least in part religious. Once the
      rebellion was over, there was a conflict between unpaid officers of the
      English army and those Old English nobles who had remained loyal to the
      English, as both sought to take possession of the “escheated,” or
      confiscated land. The English crown resolved this conflict with the
      Plantation of Munster. With establishment of the Plantation of Munster,
      large numbers of English Protestant settlers began arriving at the end of
      the 16th century. Many of them were unpaid soldiers who had been discharge
      from the English army. Those assigned to divide up the land found a
      shortage of English settlers, so in many areas the Munster Plantation
      retained Irish inhabitants. The English settlers for the most part fled
      back to England or were killed in the 9 Years War (1594-1603).
    </p>
    <h4>East Ulster</h4>
    <p>
      In the early 17th century, English settlements were established at
      Carrickfergus and Belfast by Sir Arthur Chichester and elsewhere in Antrim
      by Sir Fulke Conway, the Clotworthy’s and Langfords. In Down, the Bagenals
      and Hills established themselves as well.
      <br />
      <br />In 1605 an Irish noble with considerable land holdings in the north
      of County Down named Con O’Neill was involved in a drunken brawl in which
      an English soldier was killed. The English Lord Lieutenant of Ireland, Sir
      Arthur Chichester, had him thrown in the prison of Castle Carrickfergus
      for the offense. The wife of Con O’Neil appealed to the Scotsman, Hugh
      Montgomery, Lord of Braidstane, to get him released. Hugh Montgomery in
      turn arranged for a relative, Thomas Montgomery, to ingratiate himself to
      the keeper and garrison of the castle. Thomas also managed to ingratiate
      himself to the keeper’s daughter, whom he later married. Con was able to
      escape after being provided with a rope while the garrison was drunk from
      wine and ale provided by Thomas Montgomery. Hugh Montgomery, later styled
      Lord Montgomery of the Ards and an influential associate, Sir James
      Hamilton, later styled Viscount Clanboyne, arranged for a royal pardon for
      Con O’Neill. The royal pardon and audience with James I cost O’Neill all
      of his land, which Montgomery and Hamilton used to establish a
      predominantly Scottish plantation. Those Scots who came to settle on
      Montgomery’s and Hamilton’s plantations tended to come from the south or
      lowlands of Scotland and in many cases had a pre-existing relationship
      either as tenants or family with these two men or their undertakers.
      Theses settlers brought their Presbyterianism with them and relied
      primarily on a pastoral economy
    </p>
    <h4>The Ulster Plantation</h4>
    <p>
      With the end of the 9 Years War in 1603, the Earl of Tyrone made his
      submission to James I and VII. Tyrone’s minimal loss of land as a result
      of his rebellion was not satisfactory to those English who hoped for a
      major confiscation to their benefit. Tyrone’s departure with his followers
      for Spain, described as the “Flight of the Earl’s” in 1607 set the stage
      for the Plantation of Ulster on the lands subsequently abandoned and
      confiscated from the absent Tyrone. The land confiscated from Tyrone was
      augmented by land confiscated from Cahir O’Dogherty following his brief
      failed uprising in 1607. An interesting footnote to O’Dogherty’s uprising
      is the deployment of two Scottish companies in June 1607 under Patrick
      Crawford and William Stewart in order to put down the uprising. These
      companies were sent by Scotland at the request of England. Both men
      received land grants and presumably most of their men remained in Ulster.
      <br />
      <br />The establishment of the Ulster plantation in 1610 resulted in large
      numbers of English and Scots settling in west and central Ulster.
      Undertakers divided up the land and sold it or rented it to Scots and
      English settlers. Servitors were given land grants directly as a result of
      service, as were universities.
      <br />
      <br />The Scottish settlers of the Plantation were concentrated in Armagh,
      Cavan, Donegal, Fermanagh and Tyrone Counties. The Ulster plantation was a
      means of removing troublesome Scottish Presbyterian Dissenters and Lowland
      Scots Rievers from the border marches with England. Population pressures
      on England were relieved by the establishment and settling of the
      Plantations as well, although it proved difficult to convince the English
      to settle in Ireland. Much of the land originally intended for English
      settlers ended up in the hands of the far more numerous Scots and Irish.
      The Scots that went to the Ulster plantation also brought their
      Presbyterianism with them and relied on a pastoral way of life. Despite
      their numerical superiority and adaptability of the Scots to conditions in
      Ireland, the vast majority of positions of responsibility and prestige
      were consistently occupied by Englishmen.
      <br />
      <br />Native Irish who were permitted to live in the Plantation were
      relatively few in number and subject to paying exorbitant rent to Scots
      and English landlords. The resentment among the native Irish caused by the
      plantations was considerable and this resentment was a factor in popular
      support for the rebellion of 1641. Religious, cultural and social
      differences as well as a sense of impending persecution from increasingly
      radicalized Protestants were doubtless contributing factors as well.
    </p>
    <h4>1633-1641</h4>
    <p>
      The already tense situation in Ireland was exacerbated when Charles I’s
      Lord Deputy, Thomas Wentworth, arrived in Dublin in 1633 with the
      objectives of increasing Crown revenue, reducing the influence of
      Catholics and enforcing religious conformity on the Protestants, all goals
      bound to increase disaffection in every part of Irish society. Wentworth
      took advantage of what was, in effect, a bribe of the native Irish and Old
      English promulgated by his predecessor over a list of rights for Catholics
      called “the Graces.” The Graces would, among other things, allow for no
      changes in current land ownership as well as limited political rights for
      Catholics. The price of the Graces was a government subsidy that Catholics
      were already paying in hopes of legal recognition of the Graces.
      Wentworth, and the English Crown, wanting to create another plantation in
      Connacht and avoiding more opposition from the Protestants was unwilling
      to grant legal status to the Graces. They were however, more than willing
      to continue to take money from the Catholics while leading them to believe
      there was hope of legal recognition. The dissolution of the Irish
      Parliament in August of 1641 on the verge of approving the Graces after
      numerous attempts in previous Irish Parliaments made it clear that there
      was no intention on the part of the English administration to ever approve
      them.
      <br />
      <br />The English effort to impose an Anglican prayer book and liturgy in
      Scotland had dire consequences when Scotland revolted against the
      imposition and challenged England militarily in the two Bishops Wars of
      1639 and 1640. Both were limited wars of short duration. The English lost
      strategically because they were unable to mobilize sufficient well trained
      forces and coordinate their movements in England and Ireland against
      Scottish forces. Reluctant support by an English Parliament filled with
      those who felt some affinity with Presbyterian Scotland and a half hearted
      prosecution of the war by all sectors of society had an adverse impact as
      well. The treaties between England and Scotland granted considerable
      concessions to Scotland in religious self determination.
      <br />
      <br />The concessions made to Scotland concerning religious conformity did
      not apply to the Ulster Scots. In April of 1640 Wentworth created an army
      in Ireland, ostensibly for service in the 1640 Bishops War. Concerned
      about the loyalty and actions of the Ulster Scots, he sent it to Ulster to
      maintain order as well as pre-position for embarkation for Scotland. The
      army lacked the resources to be self supporting and ended up causing chaos
      and hardship as it preyed upon the people of the province. He dismissed
      any “nonconformist” or Presbyterian clergy from the Protestant Church of
      Ireland and also required that, in May of 1639, all take what has become
      known as the “Black Oath”, requiring that Scots swear to support Charles
      I. The loyalty of the Ulster Scots to the English monarchy was further
      damaged by a well publicized plot by Charles I to support the Catholic
      Randall MacDonnell, Earl of Antrim in his effort to conduct military
      operation against his hereditary foe, the Protestant Earl of Argyle and
      his Campbell adherents.
      <br />
      <br />In the Bishops Wars, Charles I raised the specter of using Irish
      Catholic soldiers with Catholic and Protestant officers in Britain for the
      first time, causing considerable unease among his more radical Protestant
      subjects who feared not only a Catholic army but an army loyal to the king
      that they could not control through Parliament. The unexecuted plans of
      the Bishops War presaged events to follow. The Earl of Antrim had plans to
      invade the western highlands on behalf of Charles I and the Scottish
      Parliament planned to occupy Ulster.
      <br />
      <br />Wentworth’s army in Ulster remained in place after his departure for
      England in 1640 and was to be commanded by Ormond, his eventual successor.
      The events of the Bishops War made Wentworth’s army unnecessary. In May of
      1641 a public proclamation of the dissolution of this Irish Army was made,
      with decisions still to be made about which regiments would disband, stay
      in Ireland or go into French or Spanish service. It was in the context of
      this confusion that the conspiracy for the Irish Rebellion took shape.
      Irish Catholic mercenary officers began returning from Flanders and France
      under the guise of transferring units into foreign service, but in reality
      were returning to assist in an Irish rebellion. The literature indicates a
      possibility that Charles I also had plans for his Irish Army that did not
      include foreign service but did include capture of Dublin Castle, a
      declaration by the Irish Parliament for the King against the English
      Parliament and mobilization of Ireland on his behalf. If so, the King’s
      conspiracy was overtaken by the native Irish conspiracy that resulted in
      the Rebellion of 1641.
    </p>
    <h4>1641, The Rebellion</h4>
    <p>
      The events of October 1641 in Ireland have been described as a “rising” or
      “rebellion” and perhaps even other terms, depending on the author’s bias
      and perspective on the legitimacy of the event. I will use the term
      rebellion and rebel, not in a pejorative manner but to describe a
      secretive military plan and execution occurring without the approval or
      knowledge of the government of Charles I and those involved in aforesaid
      plan and execution.
      <br />
      <br />It is likely that the Irish Catholics were encouraged by the results
      achieved by the Scots in the Bishops Wars, seeing no reason for the same
      kind of concessions from England after a successful military action. The
      original conspiracy for rebellion in Ireland foresaw a rapid takeover of
      the government with Dublin Castle, the seat of English power, as a primary
      target. Speed of execution would, in the conspirators view, limit the
      response from opposition in Ireland and prevent reinforcements from
      England arriving in time to change the outcome. As events developed, the
      original conspiracy failed in its objectives and was accompanied by a
      disorganized, disorderly, and vicious popular Irish uprising.
      <br />
      <br />A drinking bout in Dublin resulted in discovery of the attempt to be
      made on Dublin Castle and its subsequent failure on 22 October 1641. This
      failure proved critical not just for political reasons but for logistical
      reasons as well because of the black powder in the castle, which the
      rebels desperately needed and was eventually made available to those
      opposing them. On the 23rd, Sir Philem O’Neil captured Charlemont and
      Dungannon. Within a few days the rebels controlled much of Ulster with the
      exception of Antrim, Carrickfergus, northern Down, Londonderry,
      Enniskillen and some isolated castles and forts. In Leinster, Dublin and
      Drogheda held out, as did Cork, Kinsale, Youghal and Limerick in Munster
      and Galway in Connaught. Initially the rebels spared the Scots settlers in
      an effort to show common cause with them against the English Protestants.
      This effort either failed in the disorganized conduct of the rebels
      against any and all settlers and the defensive posture adopted by the
      Scots settlers who distrusted the rebels. The massacre of the Scottish
      garrison of Augher, left in place by Sir William Stewart in his movement
      to Derry, and the rapid dissemination of news of this event also did much
      to dispel any hope of Scottish neutrality in the coming struggle.
      <br />
      <br />On the 4th of November the Irish Catholic leader Sir Philem O’Neil
      produced a commission, supposedly signed by Charles I, calling for armed
      resistance in the King’s name. The literature tends to discount the
      validity of this claim, but suffice it to say that it was believed by many
      at the time to be an authentic document. If this was an effort to provide
      legitimacy to the rebellion and garner support among the protestants, it
      failed. It only served to increase Protestant suspicion of Charles I among
      those who chose to believe it was a valid.
      <br />
      <br />Initially the prominent Old English and native Irish aristocracy
      withheld overt support for the rebellion. Revulsion at the role and
      conduct of the common people was at least a contributing factor. The Old
      English initial response was to some degree perhaps influenced by a
      measure of conservatism and a property owner’s natural concern over
      repercussions if the revolt failed.
      <br />
      <br />By early November the Earl of Ormond, newly appointed Lieutenant
      General of the Army in Ireland, began recalling the army to Dublin and
      forming new regiments commanded by Sir Henry Tichbourne, Sir Charles
      Coote, Sir Piers Crosby and Lord Lambard to defend Dublin and Drogheda.
      Commissions from Charles I were sent to Scottish settlers Sir Hugh
      Montgomery, Lord of the Ards, Sir James Montgomery, Sir William Stewart
      and Sir Robert Stewart and the English settlers Sir William Cole and Sir
      Ralph Gore in Ulster to raise regiments for defense against the rebels.
      The rebellion spread in Ulster, resulting in the capture of Dundalk and
      Lisburn near Belfast. The rebels were prevented from overrunning other
      Protestant settlements as a result of the efforts of Sir Robert Stewart
      and his Laggan Army, comprised of many leaders and men who were veterans
      of the 30 Years War, in and around Derry as well as Sir George Rawdon and
      Lord Conway operating in east Ulster. Resistance against the uprising held
      out, but there was no coordinated attempt to carry out offensive
      operations of any scale against the rebels. Ormond was waiting for relief
      from England.
      <br />
      <br />England was under considerable pressure to provide an adequate
      military response to the crisis in Ireland but was on the verge of civil
      war and so unable to take action. The solution was found in the Scottish
      Army of the Solemn League. The Scottish Army at the time had proven its
      worth, both against the English Army and the civil strife of the Bishops
      Wars. Led, and in many cases manned, by veterans of the 30 Years War and
      80 Years War on the continent, it was a brutal force. Not long after the
      beginning of the rebellion, Parliament was starting the long negotiations
      with Scotland over the command, pay, logistical support, area of
      operations and port of entry that would eventually result in the
      deployment of the Scottish Army of Ulster to Ireland.
      <br />
      <br />By late November the threat from the rebels was sufficient to cause
      two columns to be sent out from Dublin. One was sent under the command of
      Sergeant Major Roper to reinforce Drogheda which was ambushed and routed
      at Julianstown bridge despite warnings that the enemy was near. The
      expedition to Wicklow led by Sir Charles Coote garrisoned New Castle and
      relieved Wicklow Castle, with brutal force. The Government’s loss at
      Julianstown, brutal victory at Wicklow and unwillingness to negotiate on
      the rights of Catholics to hold office and practice their religion led
      those remaining Old English Catholic and Irish into supporting the
      rebellion, in many cases reluctantly. In an effort to relieve the pressure
      on besieged Drogheda, Sir Charles Coote, the Mayor of Dublin, was killed
      while routing rebels at Swords and Captain Armstrong scattered a rebel
      encampment at Rathcoole in December.
      <br />
      <br />In the first year of the rebellion the military conflict was carried
      out by local levies on both sides. The leadership roles were, at least in
      some cases, filled by professionally trained soldiers who had seen service
      in the 30 Years War and 80 Years War on the continent. This prior
      experience contributed to the brutal nature of the conflict, especially
      when combined with the sectarian hatred between the Catholics and
      Protestants. There are a variety of claims in the literature on the
      massacres conducted by all parties in this conflict. It is enough, for the
      purposes of this brief narrative to say that there were massacres and
      wanton destruction of property committed, which served as a justification
      in the minds of some for even more acts of destruction and cruelty.
    </p>
    <h4>1642</h4>
    <p>
      The Catholic Earl of Antrim had managed to prevent the rebellion from
      spreading into Antrim and north Down, remaining neutral in the early
      stages of the rebellion and even going so far as to raising a regiment
      with Protestant and Catholic companies to fight the rebels. Two Catholic
      company commanders, Alasdair MacColla and Tirlough O’Cahan turned on their
      Protestant compatriots and besieged their former commander Archibald
      Stewart in Coleraine but were forced to lift the siege due to a lack of
      artillery and the approach of the Army of Ulster.
      <br />
      <br />The government’s first major expedition took place on 1-3 February
      with the unopposed burning of Newcastle and plunder of Naas. Later that
      same month, Lieutenant Colonel Monck landed with the Lord Lieutenant’s
      Regiment but no supplies or provisions accompanied it from England. It was
      soon in action taking Kilsallagan Castle north of Dublin. The besieged
      garrison of Coleraine was attacked while on a foraging expedition outside
      the city walls on 11 February in an event called ‘Black Friday.” By March
      the besieged Drogheda forces of Sir Henry Tichbourne had gone on the
      offensive taking Ardee and Dundalk and Carlingford in May. Sir Robert
      Stewart went on the offensive that same month in northwest Ulster turning
      a rebel ambush into a route at Barnesmore, capturing Strabane and routing
      the rebels in a battle at Glenmaquin in Donegal, thus reducing military
      activity of the rebels in Ulster to small unit actions.
      <br />
      <br />The Scots Army of Ulster, paid for by the English Parliament, landed
      at Carrickfergus on 3 April and held its first muster on 7 April. The
      Scottish expedition sent to Ireland was initially led by Alexander Leslie,
      a former General in the Swedish Army, who soon departed leaving it in the
      hands of Sir Robert Monro, past commander of the Swedish Green Brigade and
      a veteran of 10 years service on the Continent. By August, all the
      regiments were in Ireland and occupying garrisons throughout Antrim and
      Derry.
      <br />
      <br />In April, Ormond marched to Naas and spreading devastation on his
      way to Kilcullen, Athy, relieved the castles of Carlow and Cloghrenan. His
      column was escorting refugees back to Dublin when it was ambushed by a
      larger force of rebels at Kilrish, which he subsequently defeated. Coote
      relieved Castlegeasel and Castlejordan, capturing Trim.
      <br />
      <br />The siege of Cork, garrisoned by a force under the command of Earl
      Inchiquin, was lifted and the castle of Limerick was besieged by the
      rebels.
      <br />
      <br />On 27 April, Monro led a force out of Carrickfergus towards Belfast
      and Lisburn, incorporating Ulster settler forces enroute. He then moved
      south skirmishing with rebel forces storming Loughbrickland Castle as well
      as taking and garrisoning Newry.
      <br />
      <br />As he approached Carlingford, the rebel garrison fled and
      Tichbourne, one of Monro’s subordinate commanders, sent a garrison to
      occupy it. Monro then moved back to Carrickfergus with his force divided
      into several smaller contingents to round up as much cattle as possible.
      County Down was, for the most part, subdued by this initial expedition.
      Monro’s operation was, as were all to follow, hampered by financial
      support, available supplies, an absence of improved roads, terrain that
      was inhospitable for cavalry and an enemy that would come and go with
      relative freedom striking when and where conditions made them most
      effective. In late May Monro marched north towards Coleraine to relieve it
      as well as to cut the line of retreat of the rebel forces in Antrim. On
      the 29th of May he arrived at Dunluce Castle, the home of the Earl of
      Antrim, who Monro assumed had gone over to the rebels. This assumption may
      or not have been valid. Antrim was under suspicion as a result of his
      Catholicism and because some of his followers had joined the rebellion and
      were besieging Colerain and its Protestant Scots garrison. Monro arrested
      and imprisoned Antrim. Once the siege of Coleraine had been lifted, Monro
      marched east through Antrim returning to Carrickfergus, having effectively
      cleared Antrim of rebels.
      <br />
      <br />Monro’s expedition forced the rebels into Londonderry and the Laggan
      Army area of operations. The rebel forces out of Antrim under the command
      of Alasdair MacColla and Philem O’Neil established an encampment at
      Glemaquin enroute to Ulster. On the morning of the 16th of June, Sir
      Robert Stewart’s Laggan Army forces woke the camp with harassing fires
      from a small force causing a pursuit by the rebel forces into a position
      occupied by the Laggan main body. The efforts of Alasdair MacColla and his
      Antrim Scots prevented the pursuit of Philem O’Neil’s fleeing forces.
      Stewart then defeated a force commanded by Manus O’Cahan enroute to
      assisting Monro in the lifting of the siege of Coleraine. O’Cahan was
      captured at the siege of Dungiven not long after and sent to Derry as a
      prisoner.
      <br />
      <br />In June the Scots under Munro moved towards the rebel headquarters
      at Charlemont. Finding himself low on supplies and in danger of being cut
      off, he retreated to Newry. From Newry, he sent raiding parties to collect
      rebel cattle. Indiscriminant raids in July conducted by Lord Forbes along
      the Munster coast complicated matters for Earl Inchiquin, the Protestant
      commander, threatening the neutrality of the local population. Earl
      Inchiquin defeated a larger force led by Barry at Liscarroll effectively
      making it impossible for Confederation forces to gather an army in Munster
      for another two years.
      <br />
      <br />On 7 July the Irish Catholic Confederation met at Kilkenny electing
      a General Assembly and Supreme Council. All members of the Confederation
      took an oath of allegiance to Charles I and to uphold Catholicism. The
      goals of the Confederation were to reach an agreement with Charles I
      granting full rights to Catholics, toleration for the Catholic faith and
      self-government for Ireland. This was the beginning of an organized effort
      to conduct the war across Ireland and to move beyond the previous
      uncoordinated military actions.
      <br />
      <br />In August Owen O’Neil arrived in Donegal with an experienced cadre
      of officers from the continent and was made commander of the beleaguered
      Confederation forces in Ulster. The following month Thomas Preston arrived
      at Wexford with arms and officers from the continent and was made the
      commander of the Confederation forces in Leinster. Both Owen O’Neil and
      Preston began training their forces.
      <br />
      <br />On August 22 1642 Charles I raised his standard and began the First
      Civil War. The impact on the Army of Ulster was an immediate halt of
      supplies from Parliament which had agreed to supply them in a treaty with
      Scotland. The loyalty of the Army of Ulster’s loyalty was not an issue,
      but that of the Laggan Army was soon to be. David Leslie, the Earl of
      Leven, arrived in August as well. His arrival was followed by two
      expeditions under his command, the first being essentially an unopposed
      circuit around Ulster. The second expedition was an abortive attempt to
      attack Charlemont, cut short by a lack of supplies. While Leslie’s column
      was on its circuit, Dungannon and its settler garrison fell to the rebels
      in County Tyrone. One of the more peculiar events of Leslie’s time in
      Ulster was the brief service in the Army of Ulster of Alasdair MacColla.
      Leslie departed in November leaving the Army of Ulster, once more, in
      George Monro’s command.
      <br />
      <br />Colonel Monck was sent to relieve Ballynekill in December just 18
      miles from the Confederation’s headquarters at Kilkenny. He was ambushed
      on his return to Dublin at Timahoe by a larger force led by Thomas Preston
      which he easily outmaneuvered and routed. Despite Monck’s efforts, the
      garrison at Ballynekill surrendered to the rebels.
      <br />
      <br />Throughout 1642 there were repeated efforts by Ormond and the
      English Parliament to order the Army of Ulster to conduct operations in
      the other three provinces, but to no avail. Monro was able to influence
      the Scottish settler forces in Antrim and Down who refused to respond to
      orders from Dublin or Parliament as well. It may have been possible for
      the Army of Ulster to put down the rebellion had it been willing and able
      to conduct military operations in the other three provinces.
    </p>
    <h4>1643</h4>
    <p>
      In March Ormond and Monck moved south to clear Wicklow and Wexford of
      Confederation forces. Preston moved to block the expedition at Old Ross,
      where on the 18th of March, concentrated small arms and artillery fire
      halted his forces and he was forced to withdraw. By the end of March
      Ormond was moving back to Dublin having cleared Wicklow and Wexford.
      <br />
      <br />In April the offensive capability of the Confederation forces was
      enhanced by the arrival of heavy siege artillery, courtesy of Spain and
      large subsidies from the Pope and Spain. This allowed the Confederation
      forces to provide pay and logistical support to a military force freed
      from local support. The heavy siege artillery would have been useful, had
      the road network and logistical support been available for it to be used.
      There were however a few occasions when the artillery was put to use, the
      appearance of which was enough to cause capitulation.
      <br />
      <br />In April 1643 Sir Robert Stewart was appointed governor of
      Londonderry by Ormond and in June his Laggan army defeated O’Neill at
      Clones as Owen O’Neil was attempting to withdraw from Ulster into
      Connacht. Most of Owen O’Neil’s continental veterans were lost as a result
      of the battle. It was the timely arrival of Philem O’Neil’s force out of
      Kilkenny that allowed for reinforcement and continued viability of a
      Confederation force in the region.
      <br />
      <br />May found Monro at Dumbro, massing a force to move into Armagh. He
      was in the process of conducting clearing operations and made an attempt
      to surprise Owen Roe O’Neil at Charlemont before he could be reinforced by
      Preston and Philem O’Neil. The attempt resulted in a hard fought battle,
      but no siege due to a lack of required supplies. From there Monro moved to
      Newcastle to reinforce Colonel Home’s siege. Antrim was, once more,
      captured in the process.
      <br />
      <br />Antrim was captured while negotiating support for Charles I from the
      royalist government in Dublin and the Irish rebels. He was also prepared
      to offer the Army of Ulster a bribe, failing that they were to be
      destroyed by the combined efforts of the Dublin forces and rebel Irish.
      Planning for an expedition to Scotland under Alasdair MacColla was in the
      works as well. Knowledge of his efforts doubtless had a positive influence
      on Scottish support for Parliament. Being an embarrassing prisoner, he did
      Monro the kindness of escaping again.
      <br />
      <br />In August, Monro began making plans for a siege of Charlemont, the
      capital of the Irish Confederacy, but his efforts were thwarted by the
      starling discovery of a cessation of hostilities between the rebels and
      his supposed ally the Earl of Ormond. By September 1643 a year’s cessation
      of arms between Royalists under Ormond and Irish Confederation was agreed
      to and signed. The Cessation was not recognized by Scots Army and by most
      of the Ulster Protestants. Colonel Monck remained in action up until the
      Cessation Agreement in attempting to relieve Trim.
      <br />
      <br />The war in England had made Parliamentary financial support for the
      Army of Ulster difficult to afford and so Scotland began to assume some of
      the responsibility of supporting the army, while efforts continued to get
      the English Parliament to live up to its treaty obligations. The financial
      support of the Army of Ulster continued to be a burden on both the Scots
      and English governments until its disbandment in 1648. There can be no
      doubt that the Army of Ulster was also a burden on those it was sent to
      defend and the people of Ulster paid a high price for its presence as
      well.
      <br />
      <br />On 25 September Solemn League and Covenant between Scotland and
      English Parliamentarians was agreed to, creating an alliance between the
      two, obligating the Scots to provide an army to use against the Royalists
      in return for English conformity to Presbyterian worship, or so the Scots
      thought.
    </p>
    <h4>1644</h4>
    <p>
      The cessation created an opportunity for both Ormond and the Catholic Ear
      of Antrim to reinforce Royalist forces in Scotland and England. Early in
      the year while there was much discussion about the Army of Ulster
      returning to Scotland to serve under Leven’s expedition into England,
      three regiments left Ireland for Scotland of their own accord. Sinclair’s,
      Lothian’s and Lawer’s Regiments all departed Ireland and eventually joined
      Leven in England. In 1644 Ormond sent regiments to Bristol and Chester,
      which were destroyed at Naseby and Langport. The Earl of Antrim sent three
      regiments of Irish mercenaries totaling perhaps as many as 2,000 men,
      recently released from Spanish service, to support the Marquis of Montrose
      in Scotland. Despite the transfer of forces to England and Scotland, both
      Ormond and Monro retained sufficient forces to influence events in
      Ireland. Parliament recognized Monro as the commander of all English and
      Scottish forces in Ulster and resupplied him, perhaps in the hopes that he
      could prevent other regiments from deserting Ireland. He made an attempt
      to take Dundalk and Drogheda in the name of Parliament and failed. Monro,
      concerned about Sir Arthur Chichester’s occupation of Belfast, acceptance
      of the cessation and denouncing of the covenant occupied Belfast without
      firing a shot on 14 May 1644 disbanding Chichester’s regiment in the
      process .
      <br />
      <br />The cessation caused a split in the Laggan Army, part of which
      followed the lead of Sir Robert Stewart and complied with the terms of the
      cessation. Another part of the Laggan army under the lead of Sir Thomas’s
      brother William sided with Monro and did not accept the terms of the
      cessation.
      <br />
      <br />In April, the Army of Ulster followed Scotland’s lead by agreeing to
      the Solemn League and Covenant but the Ulster settlers were divided into
      those who would and those who would not follow the Earl of Ormond and his
      royalists. With plans in the works for an invasion of England in support
      of Parliament, the Scots settlers provided many of the soldiers for the
      new Scottish Army. By June Monro, supported by elements of the Laggan
      Army, was prepared to advance toward an army led by the Earl of
      Castlehaven and moved south into Leinster, capturing Granadard and
      Longford as well as burning Kells and Navan before running low on supplies
      and withdrawing north back into Ulster. Castlehaven’s army retreated as
      Monro advanced but by July, entered Ulster. Monro advanced to Kilwarlin
      Woods waiting for Argyll’s and Leven’s regiments but in the process ran
      out of supplies delaying his advance until he could forage in the local
      area. By August once reinforced by the Lagan army and re-supplied, he
      advanced into Armagh finding himself in a stalemate with Castlehaven until
      September and unable to follow due to bad weather he withdrew, having
      forced Castlehaven south to Clones.
      <br />
      <br />Monro’s successes in Leinster may have influenced the Earl
      Inchiquin, commander of Royalist forces in Munster, to declare for
      Parliament in July, as it seemed that the royalists were unable or
      unwilling to take action to support the protestants in Ireland. This
      change gave Parliament access to the ports of Youghal, Cork and Kinsale
      giving them the option of landing and supplying their own forces in
      Ireland.
      <br />
      <br />The Army of Ulster by the end of the year once again found itself
      neglected and short on supplies and all sides wanting it to remain in
      Ireland. It began, however, to operate independent of both Scotland who
      wanted it to send more troops to Scotland which it refused to do and
      English demands that Belfast be turned over to Parliament went unheeded.
    </p>
    <h4>1645</h4>
    <p>
      Duncannon was besieged by rebel forces from January to March of 1645 and
      eventually captured, preventing Parliament from using it as a base of
      operations. Castlehaven campaigned from April to August in Munster against
      Inchiquin in an effort to eliminate the territory under his control,
      reducing his garrisons to Cork, Kinsale, Bandon and Youghal. In May of
      1645 Sir Charles Coote, son and namesake of the late Mayor of Dublin,
      arrived in Ireland with a Parliamentary commission for the Presidency of
      Connaught. With the assistance of the Laggan army he conducted an
      expedition through Galway and besieged Sligo which fell to him in July.
      August found Monro attempting to besiege Charlemont but unable to maintain
      the siege due to lack of supply.
      <br />
      <br />November of 1645 saw the arrival of the Papal Nuncio Rinuccini with
      arms, ammunition and Papal financial support for the Confederation as well
      as instructions to expel all Protestants from Ireland and establish the
      primacy of the Catholic Church. This view of the conflict made cooperation
      and negotiation with Royalist, Commonwealth and Covenanters nearly
      impossible. With his arrival there emerged three factions within the
      Confederation, with one under the influence of Rinuccini. The other two
      factions were the Supreme Council of the Confederation led by Ormond’s
      brother in law, Lord Muskerry and Nicholas Plunkett’s faction, both of
      whom were more inclined to negotiate a less “zero-sum” arrangement. Ormond
      was in negotiations with the Confederation and at the same time another
      Royalist negotiator, the Earl of Glamorgan, was attempting at the time to
      negotiate a secret treaty, both with the goal of securing forces for
      Charles I in England but with differing degrees of religious concession in
      mind for the agreement.
    </p>
    <h4>1646</h4>
    <p>
      In March of 1646 Ormond signed a secret peace agreement with the rebels
      while at the same time being urged by a desperate Charles I to negotiate
      with the Army of Ulster as well. By May, Royalist resistance in England
      had been crushed and Charles I had joined the Scottish army at Newark.
      <br />
      <br />As a result of Rinuccini’s contribution to the Confederation war
      effort, Owen O’Neil was able to assemble a force he moved towards Ulster
      in March. His route took him down the Blackwater towards Charlemont. Monro
      was on the move as well in May and planning to join forces with his
      nephew, George Monro, and a force from Coleraine. The Laggan Army was on
      the move into Connaught. There is some controversy about the reason for
      Monro’s movement, as to whether it was another of his now familiar sweeps
      through Ulster or a precursor to a major offensive operation outside
      Ulster.
      <br />
      <br />Monro became aware of O’ Neil’s force on the 4th of June as he
      crossed the Bann. This apparently came as a complete surprise and occurred
      as he was attempting to link up with the Coleraine force. O’Neil was found
      to be between the two forces and once he became aware of this O’Neil
      established a position at Benburb, a river crossing site on the
      Blackwater. Monro positioned himself to the south of O’Neil’s force along
      the rebel’s line of communications and could have not engaged him, but
      Monro was under pressure from his subordinates to engage the rebels and
      was also concerned about the Coleraine force that was as yet unaware of O’
      Neil’s force. As Monro moved up the same side of the river as Benburb,
      O’Neil’s skirmishers contributed to the exhaustion already plaguing
      Monro’s force. Monro attacked O’Neil’s left flank along the Blackwater
      late in the day but the attack stalled. His force was also unnerved by the
      arrival of rebel forces from the direction of the expected Coleraine force
      which O’Neil had scattered prior to their arrival. A standoff of at least
      two hours occurred until the Irish counter attacked, eventually breaking
      and pursuing Monro’s force. Monro lost his siege train and baggage train
      to the rebels and perhaps as many as one in three of his men. Monro was
      forced to withdraw to Carrickfergus, his field army having been rendered
      combat ineffective. From this point on, the Army of Ulster was reduced to
      a force only able to garrison Carrickfergus and Coleraine lacking an
      offensive capability.
      <br />
      <br />Owen O’Neil failed to follow up his victory, perhaps due to a lack
      of supplies which was a constant problem for armies in Ireland, but also
      perhaps knowing that he had only scattered a part of the forces available
      in Ulster that would oppose him.
      <br />
      <br />In July the peace agreement between Ormond and the Confederation
      Supreme Council was denounced by Rinuccini, who arrested those associated
      with the Peace treaty and replaced the Supreme Council, assuming its
      leadership. Owen O’Neil moved his force to Kilkenny in support of
      Rinuccini. O’Neil and Preston, under orders from the new Supreme Council,
      marched on Dublin but were unable to achieve much due to logistical
      challenges and mutual distrust.
      <br />
      <br />The failure of the Ormond peace and difficulty in holding Dublin in
      the face of Confederation forces, led the Earl of Ormond to open
      negotiations with Monro for turn over of Dublin to the Army of Ulster.
      These negotiations failed, but the negotiations with Parliament were
      successful, setting the stage for the arrival of Parliamentary forces.
    </p>
    <h4>1647</h4>
    <p>
      In February, the Scottish Army in England withdrew into Scotland and
      Charles I fell into the hands of the Parliamentarians. Parliament also
      informed the Army of Ulster that its services were no longer needed in
      Ireland, but with no agreement on pay, it remained in place. The Scottish
      Parliament protested the abduction of King Charles and Scotland and the
      King began secretly negotiating Scottish military intervention on his
      behalf in England. On 7 June 1647 Colonel Michael Jones and regiments of
      the New Model Army arrive in Ireland. On the 19th of June Ormond turned
      Dublin over to Parliament and departed Ireland for France and exile. In
      June Preston and his Leinster army, took Fort Carlow, with Fort Naas and
      Fort Maynooth falling to him in June. Preston then besieged Trim but the
      siege was lifted by Jones. On the 17 July 1647, Monck took command of
      Parliamentary and settler forces in Ireland while Coote commanded the
      Lagan Army for Parliament. A combined force of Parliamentarians under
      Jones and forces under Sir Henry Tichbourne, the governor of Drogheda,
      defeated Preston at Dugan’s Hill 8 August 1647.
      <br />
      <br />The Earl Inchiquin seized Cashel on Sep 1647 leaving no prisoners
      and defeated Taffe and Alasdair McColla’s forces at Knockanauss on 13 Nov
      1647. By November King Charles had fled to the Isle of Wight and in
      December, he and Scottish commissioners had agreed to an “Engagement”
      exchanging a Scottish invasion of England for the union of the two nations
      and three years of Presbyterianism in England. The Engagement split the
      government of Scotland between those who supported the Engagement and
      those who did not.
    </p>
    <h4>1648</h4>
    <p>
      On 3 April 1648 Lord Inchiquin renounced Parliament after the arrest of
      King Charles I, declared for King Charles I and began negations for a
      truce with the Confederation. The Supreme Council agreed to negotiate and
      declared Rinuccini, O’Neil and their followers all traitors. Rinuccini
      opposed the peace but was not supported by the majority of the Supreme
      Council whom he excommunicated for opposing him. O’Neil supported
      Rinuccini by declaring war on the Supreme Council. The Supreme Council was
      supported by Clanricarde, Preston and Taffe in its efforts to make peace
      with the royalists. O’Neil’s Ulster army marched on Kilkenny, the
      Confederation capital, but was eventually driven back by the combined
      forces of Inchiquin and Preston.
      <br />
      <br />In May the Scottish Parliament voted in favor of war with
      Parliamentarian England and began recruiting soldiers for an army to
      invade England. Robert Monro sent elements of his forces under the command
      of his nephew George Monro to reinforce the Duke of Hamilton and “the
      Engagers”, as they were known. The Engager army having invaded England,
      was destroyed at Preston in August. George Monro and his forces, left in
      reserve in Scotland, departed Edinburgh in the face of the anti-Engager
      “Wiggamore Raid” of lowland Scots on 31 August. George Monro returned to
      Ireland after Engager regime was eliminated in September.
      <br />
      <br />In September 1648 the remnants of the Army of Ulster refused to
      surrender Carrickfergus, Coleraine and Belfast to Jones and his
      Parliamentarian forces and return to Scotland. Carrickfergus was taken by
      treachery with Sir George Monro thrown in the Tower by Monck until his
      release and retirement in Ireland in 1654. The Scottish Army of Ulster was
      disbanded shortly thereafter. At least one of the regiments went into
      English service and some of the officers and soldiers of the disbanded
      regiments did as well.
    </p>
    <h4>Aftermath</h4>
    <p>
      On 30 September 1648 Ormond landed at Cork with arms and ammunition paid
      for by France and joined forces with Inchiquin. In December 1648 Scotland
      declared for Charles II and Sir Charles Coote seized Londonderry arresting
      the governor, Sir Robert Stewart. Stewart escaped to join the, now
      royalist, settler forces. Londonderry was besieged by a royalist coalition
      of Laggan, Irish Confederates, Scots and Scots Irish under Lord Montgomery
      of Ards until April 1649 when the siege was lifted by Owen O’Neil.
      <br />
      <br />17 Jan 1649 Ormond reached agreement with the Supreme Council
      offering Catholic toleration in exchange for 18,000 Irish troops for King
      Charles I but on 30 January 1649 Charles I was executed. In February of
      1649 Rinuccini departed Ireland at command of Confederation.
      <br />
      <br />In March 1649 Monck required a loyalty oath of his soldiers and
      officers. Scottish forces taken into Parliamentary service in Ireland
      resigned or were cashiered as a result of refusing to serve under the
      Regicides. In May, a 3 month ceasefire between Monck and O’Neil was
      arranged as a result of military weakness in both forces. Drogheda was
      captured by Earl Inchiquin in June 1649 and Monck was captured at the fall
      of Dundalk and surrendered to Inchiquin.
      <br />
      <br />In August of 1649, Ormond was threatening Dublin with a combined
      force of royalists and Confederation troops when he was surprised and
      defeated at Rathmines by Jones, who had been recently reinforced by four
      English regiments. This defeat set the stage for Cromwell’s arrival with
      overwhelming force to eliminate all military opposition to the
      Parliamentarians in Ireland.
    </p>
    <h4>Operational Conditions</h4>
    <p>
      The conditions in which all the armies in Ireland had a direct, and
      usually adverse, impact on the conduct of military operations. The
      inability of the local agriculture and industry to support the arming,
      massing and movement of large formations forced all armies to rely on
      support external to Ireland. The intermittent and limited nature of this
      external support made it difficult if not impossible to follow up tactical
      victories with pursuits leading to strategic overwhelming victory.
      <br />
      <br />Relations with the populace were strained when efforts were made by
      armies to rely on local support to sustain themselves, doubtless having an
      adverse impact on support for their respective causes. The damage caused
      by such a breakdown in relations cannot have helped further local
      re-supply efforts, recruiting or intelligence gathering, thus adding to
      the difficulty in any one of the armies achieving overwhelming victory.
      <br />
      <br />Certain branches of the army, i.e. artillery and horse were unable
      to operate to maximum effect. The road network in Ireland was poor thus
      making it difficult to move artillery or to mass and move large formations
      of horse from one location to another. The limited logistical support
      experienced by all armies also limited the use of artillery and horse, as
      both are far more supply intensive that foot formations.
      <br />
      <br />It was not until Cromwell’s campaign that the logistical and
      movement obstacles to military operations in Ireland were overcome.
      Cromwell used all the port facilities under his control to rearm, refit
      and re-supply his forces in Ireland. He also had no need to rely on
      logistical support from Ireland, as he had control of the sea lines of
      communication and a steady flow of logistical support from England.
    </p>
    <h4>Significance of Army of Ulster</h4>
    <p>
      The immediate effect of the arrival of the Scottish army of Ulster was the
      stabilization of Antrim and Down and the prevention of further rebel
      action there. The operations of the Army of Ulster also forced rebels to
      operate elsewhere, increasing pressure on Laggan Army in west Ulster. This
      forced shift in rebel forces did not result in increased logistical
      support for the Laggan army from Monro and did not diminish Monro’s
      expectation that the Laggan Army support his expeditions in Ulster and
      Leinster.
      <br />
      <br />The Army of Ulster was responsible for reinforcing the already
      considerable Scottish cultural foothold in Ulster by establishing first
      Irish Presbytery in 1641. This may perhaps be the greatest legacy of the
      Army of Ulster along with the number of cashiered soldiers it left behind
      in Ireland.
      <br />
      <br />The Army of Ulster was a unique force in the War of three Kingdoms
      in that it acted as an independent force, making its own decisions about
      whom it would support, much like those forces fighting on the continent in
      the 30 Years War and 80 Years War. It was a concern for the Royalist,
      Parliamentarian and Covenanter political interests during the period of
      its existence but remained true to its paymaster whoever that might be at
      the time. The army sent forces back to Scotland to counter Montrose’s
      campaign of 1644-45 and joined the Engager movement of 1648 demonstrating
      a limited force projection capability that made it a participant in
      Scottish events while still in Ireland.
    </p>
    <h4>Role of Women</h4>
    <p>
      At no time and in none of the cultures present in Ireland were women able
      to hold elective or appointed office, nor they were not permitted to vote
      in Parliamentary elections. This did not however rule out the possibility
      of influencing events and people.
      <br />
      <br />The role of women in late 16th and early 17th century native Irish
      society was limited to informal influence by virtue of social position and
      kinship. Women could and did wield considerable influence with the male
      members of their family, relationship and personality dependent.
      Aristocratic Scottish wives brought the added advantage of education,
      greater familiarity with the English and a dowry in the form of Scottish
      mercenaries. In Old English society socially prominent women were able to
      have a greater role in representing their husbands and families if
      required and were accepted in that role. In the Scots Presbyterian
      community women and men were viewed as equal in household matters. Women
      were literate and expected to teach their children to read and wielded
      considerable influence in social and religious matters.
      <br />
      <br />In the early stages of the rebellion, women rose to prominence as
      leaders and active participants. However as the rebellion became a war,
      the role of women changed. During wartime, women occupied a critical
      logistical role in armies of the 16th and 17th century assuming roles in
      cooking, foraging, washing and medical aid that were almost never
      considered a priority by commanders. The armies operating in Ireland would
      have been no different in that regard.
    </p>
    <h4>Catholic Confederation Armies</h4>
    <p>
      Initially the forces of the Irish rebellion were local levies raised by
      local nobility or members of recognized prominent families. The ability to
      raise troops was a function of prestige or position in society. Experience
      was not necessarily a criteria for command positions other than those
      associated with training soldiers such as sergeants major. Veteran
      soldiers were available but only in small numbers, and there was a
      manpower drain throughout the Confederation period as Spain and France
      were still taking military aged males off of the island to reinforce their
      own Irish mercenary formations. In the period 1644-49 Spain received
      perhaps as many as 4,000 Irishmen and France received as many as 7,000.
      <br />
      <br />Logistics played a critical role in the conduct of Confederation
      operations. There was a perpetual shortage of black powder preventing
      effective use or firearms and artillery. The presence of enemy garrisons
      in rear areas required observation if not siege and tied down forces even
      when there were insufficient resources to support siege operations. All
      the military formations in Ireland relied on external support and all
      lacked sufficient support for decisive operations but the Confederation
      suffered the most from a lack of sufficient and regular support from Spain
      and the Papacy, their primary sponsors.
      <br />
      <br />Unity of command in military operations is absolutely critical. This
      was lacking in the strategic command structure of Confederation forces
      which was characterized by infighting and dissention at the highest levels
      all the way down to, and perhaps lower than, the army command level. The
      absence of unity of command made unity of purpose and action impossible.
    </p>
    <h3>Scottish Regiments (Army of Ulster)</h3>
    <h5>Marquis of Argyle’s</h5>
    (yellow saltire on black background) <br />Ireland 1642-1644, 1645-1648
    <br />Scotland 1644
    <br />

    <span class="padding">1642 Invasion of Rathlin Island</span>
    <br />
    <span class="padding">
      1642 Garrisoned at Dunluce Castle, Ballycastle, Ballymoney Ballantree,
      Ballintoy
    </span>
    <br />
    <span class="padding">1644 Expedition to Leinster</span>
    <br />
    <span class="padding">1644 Scotland</span>
    <br />
    <span class="padding">1645 Ireland</span>
    <br />
    <span class="padding"
      >1645 Sent contingent to Scotland under Col Robert Home</span
    >
    <br />
    <span class="padding">1645 Invasion of Islay</span>
    <br />
    <span class="padding"
      >1648 Commander and Major cashiered for opposition to Engagement</span
    >
    <br />
    <span class="padding"
      >1648 Sent contingent under Col George Monro to Scotland</span
    >
    <br />
    <span class="padding"
      >1648 Disbanded by Monck after Robert Monro’s capture</span
    >
    <br />
    <br />
    <h5>Sir Mungo Campbell of Lawer’s</h5>
    (white saltire on blue background) <br />Ireland 1642- March 1644
    <br />
    <span class="padding">1642 Coleriane Expedition</span>
    <br />
    <span class="padding">1642 Templepatrick garrison</span>
    <br />
    <span class="padding">1644 Scotland</span>
    <br />
    <span class="padding">1647 Ireland</span>
    <br />
    <span class="padding">1648 Scotland</span>
    <br />
    <br />
    <h5>Earl of Lindsay’s</h5>
    Ireland 1642- 1648
    <br />
    <span class="padding">1642 Bangor Garrison</span>
    <br />
    <span class="padding">1643 Claneboys Wood expedition</span>
    <br />
    <span class="padding">1644 Leinster expedition</span>
    <br />
    <span class="padding">1644 Castlehaven campaign</span>
    <br />
    <span class="padding"
      >1645 Sent contingent to Scotland under Col Robert Home</span
    >
    <br />
    <span class="padding">1645 Benburb</span>
    <br />
    <span class="padding"
      >1648 Sent contingent under Col George Monro to Scotland</span
    >
    <br />
    <span class="padding"
      >1648 Disbanded by Monck after Robert Monro’s capture</span
    >
    <br />
    <br />
    <h5>Earl of Eglinton’s 1642-1644 /James Montgomery’s 1644-1648</h5>
    Ireland 1642-1648
    <br />
    <span class="padding">1642 Bangor garrison</span>
    <br />
    <span class="padding">1643 Claneboys Wood expedition</span>
    <br />
    <span class="padding">1644 Leinster expedition</span>
    <br />
    <span class="padding"
      >1645 Sent contingent to Scotland under Col Robert Home</span
    >
    <br />
    <span class="padding">1645 Benburb</span>
    <br />
    <span class="padding"
      >1648 Sent contingent under Col George Monro to Scotland</span
    >
    <br />
    <span class="padding"
      >1648 Disbanded by Monck after Robert Monro’s capture</span
    >
    <br />
    <br />
    <h5>Earl of Glencairn’s</h5>
    Ireland 1642-1648
    <br />
    <span class="padding">1642 Carrickfergus garrison</span>
    <br />
    <span class="padding">1643 Siege of Charlemont</span>
    <br />
    <span class="padding">1644 Leinster expedition</span>
    <br />
    <span class="padding">1644 Castlehaven campaign</span>
    <br />
    <span class="padding"
      >1645 Sent contingent to Scotland under Col Robert Home</span
    >
    <br />
    <span class="padding">1645 Benburb</span>
    <br />
    <span class="padding">1648 Ejected from Army of Ulster</span>
    <br />
    <span class="padding">1648 Joined Monck’s army</span>
    <br />
    <span class="padding"
      >1648 Assisted in capture of Robert Monro at Carrickfergus</span
    >
    <br />
    <br />
    <h5>
      Col Robert Home’s 1642-1644 /Col John Maxwell’s 1645/ Col Robert Home’s
      1646-1648
    </h5>
    (white saltire on green background) <br />(conjecture: incorporated Earl of
    Leven’s Lifeguard Company of Foot) <br />Ireland 1642-1644
    <br />

    <span class="padding">1642 Newry</span>
    <br />
    <span class="padding">1642 Leven’s expeditions</span>
    <br />
    <span class="padding">1643 Carrickfergus Garrison</span>
    <br />
    <span class="padding">1643 Claneboys Wood expedition</span>
    <br />
    <span class="padding">1643 Siege of Newcastle</span>
    <br />
    <span class="padding">1644 Seizure and occupation of Belfast</span>
    <br />
    <span class="padding">1644 Leinster expedition</span>
    <br />
    <span class="padding">1644 Castlehaven campaign</span>
    <br />
    <br />
    <h5>Scotland 1645 (in red coats)</h5>
    1646 Home returns to Ireland to command regiment <br />1645 Benburb
    <br />1648 Sent contingent under Col George Monro to Scotland <br />1648
    Disbanded by Monck after Robert Monro’s capture <br />Earl of Lothian’s
    Regiment
    <br />
    <br />
    <h5>Ireland 1642-1644</h5>
    1642 Leven’s expeditions <br />Scotland 1644
    <br />
    <br />
    <h5>Earl of Leven’s 1642 /Col George Monro’s Regt 1643-1648</h5>
    (conjectural: white saltire on blue background in upper left of a solid red
    field) <br />Ireland 1642-1648
    <br />
    <span class="padding">1642 Apperly Hills expedition</span>
    <br />
    <span class="padding">1642 Garrsion Dunluce Castle</span>
    <br />
    <span class="padding">1642 Garrison Coleraine</span>
    <br />
    <span class="padding">1642 Leven’s expeditions</span>
    <br />
    <span class="padding">1644 Leinster expedition</span>
    <br />
    <span class="padding">1644 Castlehaven campaign</span>
    <br />
    <span class="padding"
      >1645 Sent contingent to Scotland under Col Robert Home</span
    >
    <br />
    <span class="padding">1645 Benburb</span>
    <br />
    <span class="padding"
      >1648 Sent contingent under Col George Monro to Scotland</span
    >
    <br />
    <span class="padding">
      1648 Disbanded by Monck after Robert Monro’s capture and enters
      Parliamentarian service as Conways Regiment
    </span>
    <br />
    <span class="padding"
      >1648 Mutinies and elects Montgomery of Ards as commander</span
    >
    <br />
    <span class="padding"
      >1648 Joins Col George Monro in siege of Londonderry</span
    >
    <br />
    <span class="padding"
      >1649 Re-occupies Colerain after siege of Londonderry is lifted</span
    >
    <br />
    <br />
    <h5>MAJ Gen Sir Robert Monro’s 1642-1648</h5>

    <span class="padding">1642 Coleraine expedition</span>
    <br />
    <span class="padding">1644 Seizure of Belfast</span>
    <br />
    <span class="padding">1644 Leinster expedition</span>
    <br />
    <span class="padding">1644 Castlehaven campaign</span>
    <br />
    <span class="padding">1645 Benburb</span>
    <br />
    <span class="padding"
      >1648 Sent contingent under Col George Monro to Scotland</span
    >
    <br />
    <span class="padding"
      >1648 Disbanded by Monck after Robert Monro’s capture</span
    >
    <br />
    <br />
    <h5>Lord Sinclair’s Regt</h5>
    Ireland 1642-1644 <br />Scotland 1644-1647 <br />Train of Artillery, General
    Sir Alexander Hamilton <br />Horse in troop sized (100 or less) elements
    <br />
    <br />
    <h5>Settler Regiments</h5>
    Regiments of the Laggan Army (West Ulster) 1641-48 <br />Sir Robert
    Stewart’s (Scots) (Donegal/Tyrone) <br />Sir William Stewart (Scots)
    (Donegal/Tyrone) <br />Sir Ralph Gore/Colonel Audley Mervin (English)
    (Donegal) <br />Sir William Cole (English) (Enniskillen) <br />East Ulster
    Regiments 1641-48 <br />Sir James Montgomery (Scots) (Down) <br />Sir George
    Rawdon (Scots) (Down) <br />Sir John Clotworthy (English) (Antrim) <br />Sir
    Edward, Lord Conway (English) (Antrim) <br />Sir James Hamilton, Lord
    Claneboye (Scots) (Down) <br />Sir Hugh Montgomery, Lord of Ards (Scots)
    (Down) <br />Sir Arthur Chichester (English) disbanded 1644 (Antrim)
    <br />
    <br />
    <h5>Leinster Regiments 1641-48</h5>
    Sir Henry Tichbourne (English) <br />Sir Charles Coote (English) <br />Sir
    Piers Crosby (English) <br />Lord Lambard (English)
    <h4>Parliamentarian Regiments</h4>
    <h5>Munster Regiments 1642-44</h5>
    Sir William Leger, Lord President of Munster/Lord Inchiquin (English)
    <br />Sir Charles Vavasour (English) <br />Sir William Ogle (English)
    <br />Sir John Paulet (English)
    <br />
    <br />
    <h5>Connaught Regiments 1642-44</h5>
    Lord Ranelagh (English) <br />Sir Michael Earnley (English)
    <br />
    <br />
    <h5>Lienster Regiments 1642-44</h5>
    Lord Lieutenants (commanded by Col George Monck) (English) <br />Sir Simon
    Harcourt (English) <br />Sir Fulk Hunck (English) <br />Lord Lisle’s Horse
    (English)
    <hr />
    <h3>Sources:</h3>
    <ol>
      <li>
        Ashley, Maurice, General Monck, (Rowman and Littlefield, New Jersey,
        1977)
      </li>
      <li>
        Bennet, Martyn, The Civil Wars in Britain and Ireland 1638-1651,
        (Blackwell Publishing, Oxford, UK, 1997)
      </li>
      <li>
        Clarke, Aidan, The Old English In Ireland, 1625-42, (McGibbon and Kee,
        London, 1966)
      </li>
      <li>
        Cronne, H.A., et. al., ed, Essays in British and Irish History,
        (Frederick Muller, Ltd., London, 1949)
      </li>
      <li>
        Furgol, Edward M., A Regimental History of the Covenanting Armies,
        1639-1651, (John Donald Publishers, Edinburgh, 1990)
      </li>
      <li>
        Gillespie, Raymond, Colonial Ulster, The Settlement of East Ulster,
        1600-1641, ( Cork University Press, Cork, 1985)
      </li>
      <li>
        Harrison, John, The Scot in Ulster, (William Blackwood and Sons,
        Edinburgh, 1888)
      </li>
      <li>
        Kenyon, John and Ohlmeyer, ed., The Civil Wars, A Military History of
        England, Scotland and Ireland, 1638-1660, (Oxford University Press,
        Oxford, 1998)
      </li>
      <li>
        Lenihan, Padriag, Confederate Catholics at War, 1641-49, (Cork
        University Press, Cork, 2001)
      </li>
      <li>
        MacCuarta, Brian, SJ, ed., Ulster 1641, Aspects of the Rising,
        (Institute of Irish Studies, Belfast, 1993)
      </li>
      <li>
        McKenny, Kevin, The Lagan Army in Ireland, 1640-1685, (Fourcourts Press,
        Dublin, 2005)
      </li>
      <li>
        O’Dowd, Mary, A History of Women in Ireland, 1500-1800, (Pearson
        Education, Ltd., Harlow, UK, 2005)
      </li>
      <li>
        Ohlmeyer, Jane N. ed., Ireland From Independence to Occupation
        1641-1660, (Cambridge University Press, UK, 1995)
      </li>
      <li>
        Perceval-Maxwell, M., The Scottish Migration to Ulster in the Reign of
        James I, (Ulster Historical Foundation, Ireland, 1999)
      </li>
      <li>
        Perceval-Maxwell, M., The Outbreak of the Irish Rebellion of 1641,
        (McGill-Queens University Press, Canada, 1994)
      </li>
      <li>
        Reid, Stuart and Turner, Graham, Scots Armies of the English Civil War,
        (Osprey Publishing, Ltd., Oxford, 1999)
      </li>
      <li>
        Reid, Stuart, Scots Armies of the 17th Century 1: The Army of the
        Covenant, 1639-51, (Partizan Press, Essex, 2001)
      </li>
      <li>
        Reid, Stuart, Scots Armies of the 17th Century 2: Scots Colors,
        (Partizan Press, Essex, 2001)
      </li>
      <li>
        Ryder, Ian, An English Army for Ireland, (Partizan Press, Essex, 1987)
      </li>
      <li>
        Stevenson, David, Scottish Covenanters and Irish Confederates, (Ulster
        Historical Foundation, Belfast 1981)
      </li>
      <li>
        Stevenson, David, Highland Warrior, Alasdair MacColla and the Civil
        Wars,( The Saltire Society, Edinburgh, 1980)
      </li>
      <li>
        Wedgewood, C.V., Thomas Wentworth, First Earl of Strafford, 1593-1641,(
        Pheonix Press, London, 1961)
      </li>
    </ol>
    <h3>Suggested Additional Reading</h3>
    <ul>
      <li>
        Canny, Nicholas, Making Ireland British, 1580-1650, (Oxford University
        Press, Oxford, 2001)
      </li>
      <li>
        Ellis, Steven G., Ireland in the Age of the Tudors, (Longman, UK, 1998)
      </li>
      <li>
        Gardiner, S.R., History of the Great Civil War, (Volumes 1-4, Longman
        Green and Co., London, 1888)
      </li>
      <li>
        Reid, Stuart and Turner, Graham, Scots Armies of the English Civil War,
        (Osprey Publishing, Ltd., Oxford, 1999)
      </li>
      <li>
        Wiggins, Kenneth, Anatomy of a Siege, King Johns Castle, Limerick 1642,
        (Boydell Press, Woodbridge, UK, 2001)
      </li>
      <li>
        Wheeler, James Scott, The Irish and British Wars, 1637-1653, (Routledge
        Press, London, 2002)
      </li>
      <li>
        Young, John R, ed., Celtic Dimensions of the British Civil Wars, (John
        Donald Publishers, LTD. Edinburgh, 1997)
      </li>
    </ul>
  </div>
</template>

<script>
export default {};
</script>

<style lang="scss" scoped></style>
